import React from 'react';
import {shallowEqual} from 'react-redux';
import {ItemProps} from 'react-virtuoso';

import getTestProps from 'test/lib/cypress/getTestProps';

/**
 * In order to support optimized rendering of a TableRow, the <tr> tag must be memoized and have a stable
 * value.
 *
 * So this is a pure component that just renders the <tr> tag, and it is memoized so that it only re-renders
 * when the children or the props change.  This context is explicitly ignored because the children will
 * change any time they need to.
 */
export default React.memo(
  (props: ItemProps<{}> & {context: unknown}): JSX.Element => {
    const index = props['data-index'];
    const {children, item, context, ...rest} = props;
    return (
      <tr
        className="mediocre-tableEditable-tableRow"
        key={index}
        {...rest}
        {...getTestProps({component: 'TableRow', element: 'root', index: index.toString()})}
        data-amp-safe={false}
      >
        {children}
      </tr>
    );
  },
  (prevProps: Record<string, unknown>, nextProps: Record<string, unknown>) =>
    Object.keys(nextProps).every((key) => {
      if (key === 'context') {
        // Don't compare the context, because that changes so frequently.  Instead, just let the children
        // decide when to update.  The children cells are memoized, so they will only re-render if they
        // actually changed.  So we don't need to catch the context changes here.
        return true;
      }
      return shallowEqual(prevProps[key], nextProps[key]);
    })
);
